import { useState, useEffect } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Search, Landmark, AlertCircle } from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";

const TIPOS_CONTA = [
  { value: "", label: "— Não informado —" },
  { value: "corrente", label: "Conta Corrente" },
  { value: "poupanca", label: "Poupança" },
  { value: "pagamento", label: "Conta Pagamento" },
  { value: "investimento", label: "Investimento" },
  { value: "prepago", label: "Pré-pago (benefício)" },
  { value: "outro", label: "Outro" },
];

const tipoContaLabel = (v) => TIPOS_CONTA.find((t) => t.value === v)?.label || "-";

const EMPTY_FORM = {
  nome: "", codigo: "", agencia: "", conta: "", tipo_conta: "", pix: "", observacao: "", ativo: true,
  saldo_inicial: 0, data_saldo_inicial: "",
};

function BancoForm({ item, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [bancosBrasil, setBancosBrasil] = useState([]);
  const [formData, setFormData] = useState(
    item ? {
      nome: item.nome || "", codigo: item.codigo || "", agencia: item.agencia || "",
      conta: item.conta || "", tipo_conta: item.tipo_conta || "", pix: item.pix || "",
      observacao: item.observacao || "", ativo: item.ativo !== false,
      saldo_inicial: item.saldo_inicial ?? 0, data_saldo_inicial: item.data_saldo_inicial || "",
    } : EMPTY_FORM
  );

  useEffect(() => {
    axios.get(`${API}/bancos-brasil`).then((r) => setBancosBrasil(r.data || [])).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim()) { toast.error("Nome do banco é obrigatório"); return; }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        saldo_inicial: parseFloat(formData.saldo_inicial) || 0,
        data_saldo_inicial: formData.data_saldo_inicial || null,
      };
      if (item) {
        await axios.put(`${API}/bancos/${item.id}`, payload);
        toast.success("Banco atualizado!");
      } else {
        await axios.post(`${API}/bancos`, payload);
        toast.success("Banco cadastrado!");
      }
      onSuccess();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao salvar banco"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="form-banco">
      <div className="space-y-2">
        <Label>Banco *</Label>
        <div className="flex gap-2">
          <NativeSelect
            value=""
            onValueChange={(v) => {
              const b = bancosBrasil.find((x) => x.codigo === v);
              if (b) setFormData({ ...formData, nome: b.nome === "Outro" ? "" : b.nome, codigo: b.codigo === "000" ? "" : b.codigo });
            }}
            placeholder="Selecionar da lista…"
            options={bancosBrasil.map((b) => ({ value: b.codigo, label: `${b.codigo} - ${b.nome}` }))}
            className="w-[200px]"
            data-testid="select-banco-brasil"
          />
          <Input
            value={formData.nome}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            placeholder="Nome do banco"
            required
            className="flex-1"
            data-testid="input-banco-nome"
          />
        </div>
        <p className="text-[11px] text-slate-500">Selecione na lista para preencher automaticamente ou digite o nome.</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label>Código</Label>
          <Input
            value={formData.codigo}
            onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
            placeholder="Ex: 341"
            data-testid="input-banco-codigo"
          />
        </div>
        <div className="space-y-2">
          <Label>Agência</Label>
          <Input
            value={formData.agencia}
            onChange={(e) => setFormData({ ...formData, agencia: e.target.value })}
            placeholder="Ex: 0001"
            data-testid="input-banco-agencia"
          />
        </div>
        <div className="space-y-2">
          <Label>Conta</Label>
          <Input
            value={formData.conta}
            onChange={(e) => setFormData({ ...formData, conta: e.target.value })}
            placeholder="Ex: 12345-6"
            data-testid="input-banco-conta"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Tipo de Conta</Label>
          <NativeSelect
            value={formData.tipo_conta}
            onValueChange={(v) => setFormData({ ...formData, tipo_conta: v })}
            options={TIPOS_CONTA}
            data-testid="select-banco-tipo-conta"
          />
        </div>
        <div className="space-y-2">
          <Label>Chave PIX</Label>
          <Input
            value={formData.pix}
            onChange={(e) => setFormData({ ...formData, pix: e.target.value })}
            placeholder="Opcional"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50/50">
        <div className="space-y-2">
          <Label>Saldo Inicial (R$)</Label>
          <Input
            type="number" step="0.01"
            value={formData.saldo_inicial}
            onChange={(e) => setFormData({ ...formData, saldo_inicial: e.target.value })}
            placeholder="0,00"
            data-testid="input-banco-saldo-inicial"
          />
        </div>
        <div className="space-y-2">
          <Label>Data do Saldo Inicial</Label>
          <Input
            type="date"
            value={formData.data_saldo_inicial}
            onChange={(e) => setFormData({ ...formData, data_saldo_inicial: e.target.value })}
            data-testid="input-banco-data-saldo"
          />
        </div>
        <p className="col-span-2 text-[11px] text-emerald-800">
          Ponto de partida da conciliação bancária: saldo da conta na data informada.
        </p>
      </div>

      <div className="space-y-2">
        <Label>Observação</Label>
        <Textarea
          value={formData.observacao}
          onChange={(e) => setFormData({ ...formData, observacao: e.target.value })}
          placeholder="Observações adicionais..."
          rows={2}
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={formData.ativo}
          onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
          data-testid="checkbox-banco-ativo"
        />
        Banco <b>ativo</b> (disponível para seleção nos lançamentos)
      </label>

      <div className="flex gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">Cancelar</Button>
        <Button
          type="submit"
          className="flex-1 bg-luvcorp-navy hover:bg-luvcorp-navy-dark text-white"
          disabled={loading}
          data-testid="btn-salvar-banco"
        >
          {loading ? "Salvando..." : item ? "Atualizar" : "Cadastrar Banco"}
        </Button>
      </div>
    </form>
  );
}

export default function BancosPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/bancos`);
      setItems(r.data || []);
    } catch {
      toast.error("Erro ao carregar bancos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleDelete = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/bancos/${deleteItem.id}`);
      toast.success("Banco excluído!");
      setDeleteItem(null);
      fetchItems();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao excluir banco"));
    } finally {
      setDeleting(false);
    }
  };

  const filtered = items.filter((i) => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return true;
    return (
      i.nome?.toLowerCase().includes(q) ||
      i.codigo?.toLowerCase().includes(q) ||
      i.agencia?.toLowerCase().includes(q) ||
      i.conta?.toLowerCase().includes(q)
    );
  });

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="bancos-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-luvcorp-navy flex items-center gap-2">
              <Landmark className="h-6 w-6 text-luvcorp-gold" />
              Cadastro de Bancos
            </h1>
            <p className="text-slate-500">Bancos pagadores e recebedores usados nos lançamentos financeiros</p>
          </div>
          <Button
            className="bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white"
            onClick={() => { setEditItem(null); setShowForm(true); }}
            data-testid="btn-novo-banco"
          >
            <Plus className="h-4 w-4 mr-2" />
            Novo Banco
          </Button>
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar por nome, código, agência ou conta…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="input-search-bancos"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Banco</TableHead>
                  <TableHead>Código</TableHead>
                  <TableHead>Agência</TableHead>
                  <TableHead>Conta</TableHead>
                  <TableHead>Tipo de Conta</TableHead>
                  <TableHead className="text-right">Saldo Inicial</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[100px]">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-slate-500">Carregando…</TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                      <Landmark className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      Nenhum banco cadastrado
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((item) => (
                    <TableRow key={item.id} data-testid={`banco-row-${item.id}`}>
                      <TableCell className="font-medium">{item.nome}</TableCell>
                      <TableCell className="font-mono text-sm">{item.codigo || "-"}</TableCell>
                      <TableCell className="font-mono text-sm">{item.agencia || "-"}</TableCell>
                      <TableCell className="font-mono text-sm">{item.conta || "-"}</TableCell>
                      <TableCell>{tipoContaLabel(item.tipo_conta)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.saldo_inicial || 0)}
                        {item.data_saldo_inicial && (
                          <span className="block text-[10px] text-slate-400">em {item.data_saldo_inicial.split('-').reverse().join('/')}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={item.ativo !== false ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}>
                          {item.ativo !== false ? "Ativo" : "Inativo"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => { setEditItem(item); setShowForm(true); }}
                            title="Editar"
                            data-testid={`btn-edit-banco-${item.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost" size="sm" className="text-red-500"
                            onClick={() => setDeleteItem(item)}
                            title="Excluir"
                            data-testid={`btn-delete-banco-${item.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-lg" data-testid="dialog-banco-form">
            <DialogHeader>
              <DialogTitle>{editItem ? "Editar Banco" : "Novo Banco"}</DialogTitle>
            </DialogHeader>
            <BancoForm
              item={editItem}
              onSuccess={() => { setShowForm(false); fetchItems(); }}
              onCancel={() => setShowForm(false)}
            />
          </DialogContent>
        </Dialog>

        <Dialog open={!!deleteItem} onOpenChange={(o) => !o && setDeleteItem(null)}>
          <DialogContent className="max-w-md" data-testid="dialog-delete-banco">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />Excluir Banco
              </DialogTitle>
              <DialogDescription className="text-slate-700 pt-2">
                Deseja excluir o banco <b>{deleteItem?.nome}</b>? Bancos vinculados a lançamentos não podem ser excluídos — nesse caso, desative-o.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteItem(null)} disabled={deleting}>Cancelar</Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={handleDelete}
                disabled={deleting}
                data-testid="btn-confirm-delete-banco"
              >
                {deleting ? "Excluindo..." : "Excluir"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
